import { isAxiosError } from "axios";
import { ChoicesRestful, settings } from "survey-core";
import { getApiClient } from "@/lib/api/apiClient";

// The members touched below are `protected`/private in survey-core's typings
// but are the same seams the shared package's `registerChoicesByUrl` and
// survey-core's own XHR/fetch implementations use (see
// `choicesRestful.d.ts` and the compiled `survey.core.js`). Accessed through
// a narrow typed view instead of `any` so each touch point is explicit.
interface ChoicesRestfulInternals {
  beforeSendRequest(): void;
  beforeLoadRequest(): void;
  onLoad(result: unknown, loadingObjHash: string | null): void;
  onError(status: string, response: string): void;
  readonly processedUrl: string;
  readonly processedPath: string;
  readonly objHash: string;
  allowEmptyResponse: boolean;
  readonly owner: {
    getPropertyValue(name: string): unknown;
  } | null;
}

interface ChoicesRestfulPrototype extends ChoicesRestfulInternals {
  sendRequest(): void;
}

const proto = ChoicesRestful.prototype as unknown as ChoicesRestfulPrototype;

interface BriefRow {
  readonly type?: unknown;
}

/**
 * Narrows the rows inside a `choicesByUrl` response payload to the ones
 * matching the question's `companyType` (a RumbleDirect schema property
 * registered on the `dropdown` class — the brief endpoints return one mixed
 * list per scope). `path` is survey-core's already-processed `choicesByUrl
 * .path`: the payload is filtered at that key so the result handed to
 * `ChoicesRestful.onLoad` still has the shape the schema describes, and
 * onLoad's own path walk unwraps it as usual. Absent `companyType` (or a
 * payload that does not carry an array at `path`) means "no narrowing" — the
 * payload passes through untouched and survey-core's own empty-response
 * handling applies.
 *
 * Only the single-level contract in play is supported: the seeded schemas
 * all use `path: "member"`, so the envelope is rebuilt at that one key. A
 * nested path would need survey-core's own separators (`;`/`,`, see
 * `ChoicesRestful.getPathes`) rather than a dotted walk — there is no such
 * schema, so there is no such walker.
 *
 * Runs on the raw payload (before survey-core maps `valueName`/`titleName`
 * and drops `type`) because survey-core's per-URL static cache — when it is
 * enabled — replays already-mapped `ItemValue`s: narrowing had to happen
 * before that mapping, and before anything can serve a stale un-narrowed
 * mapping to a later question.
 */
export function narrowChoicesByCompanyType(
  payload: unknown,
  companyType: unknown,
  path: string,
): unknown {
  if (typeof companyType !== "string") return payload;
  if (typeof payload !== "object" || payload === null) return payload;

  const envelope = payload as Record<string, unknown>;
  const rows = envelope[path];
  if (!Array.isArray(rows)) return payload;

  return {
    ...envelope,
    [path]: rows.filter(
      (row) =>
        typeof row === "object" &&
        row !== null &&
        (row as BriefRow).type === companyType,
    ),
  };
}

/**
 * Routes `choicesByUrl` fetches through admin-v2's shared axios client so
 * they carry the bearer token and the tenant scope (`/affiliates/{id}`), and
 * applies the question's optional `companyType` narrowing.
 *
 * Reimplemented rather than imported from `@rumbledirect/survey`: that
 * package exposes a barrel-only export whose transitive imports pull in antd,
 * which admin-v2 deliberately keeps out of its dependency graph. The request
 * override is the same idea (prototype-level `ChoicesRestful.sendRequest`),
 * but goes through `getApiClient()` — admin-v2's configured client.
 *
 * Caching: survey-core's static per-URL `itemsResult` cache and its
 * `sendingSameRequests` coalescing registry are deliberately disabled via
 * the public `settings.web.cacheLoadedChoices = false`. Both are keyed by
 * `objHash` alone — the processed URL plus path and name mappings, *not* our
 * `companyType` — so with the seeded schema's two same-URL dropdowns
 * (`leaseCompany`/`insuranceCompany` on `/admin/companies/brief`) the second
 * question would be served the first's *narrowed* rows. Verified against
 * survey-core 2.5.0 (`survey.core.js`): the public setting backs the
 * instance-level `isUsingCache` getter unless the instance's own
 * `isUsingCacheFromUrl` is set (we never set it), and every one of the three
 * touch points gates on `isUsingCache` — `addSameRequest` (the coalescing
 * registry), `useChangedItemsResults` → `getCachedItemsResult` (the per-URL
 * cache read, via `itemsResult[hash]` being always undefined) and the cache
 * write in `onLoad`. So one public flag covers both the cache and the
 * coalescing. The flag is global rather than per-instance: nothing else in
 * the app uses `choicesByUrl`, and the choices machinery only consults it at
 * fetch time. With the cache off, the three same-URL questions issue one
 * request each; the payload is small and the endpoint is cheap, and
 * correctness of the narrowing is not negotiable. (The app's React Query
 * cache is not reachable from this seam without bypassing survey-core's
 * choice pipeline entirely.)
 */
export const registerChoicesByUrl = (): void => {
  // Global, and must land before any model with a `choicesByUrl` question is
  // constructed: `ChoicesRestful` instances are created during `new Model()`
  // and read the setting through the `isUsingCache` getter from their first
  // `run()`. This call site sits inside `registerSurveyExtensions()`, which
  // runs at module scope of `SurveyForm.tsx` — before any model exists.
  settings.web.cacheLoadedChoices = false;

  proto.sendRequest = function (this: ChoicesRestfulPrototype) {
    this.beforeSendRequest();

    // `companyType` narrowing can legitimately zero out a valid response
    // (e.g. a tenant with companies but none of the requested type).
    // survey-core's default `onLoad` treats an empty result as a request
    // failure (`WebRequestEmptyError`) unless this is set, which would turn
    // a correct empty dropdown into a spurious error message.
    this.allowEmptyResponse = true;

    // Captured before dispatch, not read from `this.objHash` after the
    // response resolves: `objHash` can change while a request is in flight
    // (e.g. a URL expression re-evaluating), and survey-core's own
    // `callResultCallback` compares the hash it is given against the
    // instance's *current* hash to decide whether a response is stale. Handing
    // it `this.objHash` post-resolution makes that comparison always equal,
    // defeating the guard. The shared package's override captures it the same
    // way (`registerChoicesByUrl.ts:27`).
    const loadingObjHash = this.objHash;

    // `getApiClient()` is called inside the chain, not before it: it throws
    // synchronously if the API config is not ready (e.g. `VITE_APP_API_DOMAIN`
    // unset), and survey-core's `runChoicesByUrl` has no try/catch around
    // `choicesByUrl.run()` — a synchronous throw here would crash `new
    // Model()` entirely instead of landing in `.catch()` as a per-question
    // error, the same way a network failure does.
    Promise.resolve()
      .then(() => getApiClient().get<unknown>(this.processedUrl))
      .then((response) => {
        this.beforeLoadRequest();
        this.onLoad(
          narrowChoicesByCompanyType(
            response.data,
            this.owner?.getPropertyValue("companyType"),
            this.processedPath,
          ),
          loadingObjHash,
        );
      })
      .catch((error: unknown) => {
        this.beforeLoadRequest();
        this.onError(
          error instanceof Error ? error.message : "Request failed",
          isAxiosError(error) && typeof error.response?.data === "string"
            ? error.response.data
            : "",
        );
      });
  };
};
